import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { setUserProperties } from '@sophocles/analytics';
import { initializeTenant } from '@sophocles/app/tenant';
import { applyThemeRouting, THEME_PREFERENCE_KEY } from '@sophocles/app/themeRouting';
import useRoutePersistence from '@sophocles/app/useRoutePersistence';
import { loadReference } from '@sophocles/features/reference';
import { loadUserPreferences, selectUserPreferenceValue } from '@sophocles/features/userPreferences';
import checkCredentials from '@sophocles/router/checkCredentials';
import ApplicationRouter from '@sophocles/router/routes';
import { initializeSockets } from '@sophocles/socket-io/slice';

const MainLayout = props => {
  const dispatch = useDispatch();
  const [currentUser, setCurrentUser] = useState(false);

  const { t } = useTranslation();

  // On mobile, persist the current route on every navigation and
  // restore from storage if a cold boot drops the user on a home
  // path. Auth must be ready before we restore so the route guards
  // run with a populated store.
  useRoutePersistence(!!currentUser);

  useEffect(() => {
    dispatch(initializeSockets(t)); // Initialize sockets on app startup
  }, [dispatch]);

  // Run tenant init + the initial credentials check in the background.
  // ApplicationRouter renders immediately; RequireAuth on the first
  // route does its own checkCredentials() call, so the only thing we
  // lose by not gating on this is a brief paint with default theme
  // colors before initializeTenant resolves the tenant swatches.
  // Tradeoff intentional — eliminates a redundant loader phase.
  useEffect(() => {
    initializeTenant().then(() => {
      checkCredentials().then(user => {
        setCurrentUser(user);
        if (user != null) {
          setUserProperties(user);
        }
        if (user) {
          dispatch(loadUserPreferences());
          dispatch(loadReference());
        }
      });
    });
  }, [dispatch]);

  // The edge selects the portal build from the ui_theme cookie, so a stored
  // theme preference only takes effect once the cookie agrees with it. Until
  // the preference has loaded this reads undefined and nothing moves.
  const storedTheme = useSelector(selectUserPreferenceValue(THEME_PREFERENCE_KEY));

  useEffect(() => {
    if (!currentUser) return;
    if (applyThemeRouting(storedTheme)) {
      window.location.reload();
    }
  }, [currentUser, storedTheme]);

  return <ApplicationRouter />;
};

export default MainLayout;
